import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  toast,
} from "react-toastify";

import {
  ImagePlus,
  X,
  ArrowLeft,
  MapPin,
  Phone,
  Tag,
  Package,
  ShieldCheck,
  Truck,
  Loader2,
  Check,
  CheckCircle2,
} from "lucide-react";

import axios from "axios";

import {
  useAddQolbaqJsonMutation,
} from "../redux/slices/qolbaqApiSlice";

import {
  addQolbaq,
} from "../redux/slices/qolbaqSlice";


/*
|--------------------------------------------------------------------------
| Sabitlər
|--------------------------------------------------------------------------
*/

const MAX_IMAGES = 10;

// Pulsuz elan limiti
const MAX_FREE_ADS = 3;

// Frontend-də qəbul edilən bütün şəkil formatları
const ALLOWED_IMAGE_EXTENSIONS = new Set([
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".heic",
  ".heif",
  ".avif",
  ".bmp",
  ".tiff",
  ".tif",
  ".svg",
]);

const isSupportedImageFile = (file) => {
  if (!file) return false;

  const fileName = String(file.name || "").toLowerCase();
  const dotIndex = fileName.lastIndexOf(".");
  const extension =
    dotIndex >= 0 ? fileName.slice(dotIndex) : "";

  // Normal image MIME type və ya yuxarıdakı icazəli extension
  // HEIC/HEIF kimi bəzi formatlarda browser MIME-i boş göndərə bilər.
  return (
    String(file.type || "").toLowerCase().startsWith("image/") ||
    ALLOWED_IMAGE_EXTENSIONS.has(extension)
  );
};


/*
 *--------------------------------------------------------------------------
 * Şəkil compression
 *--------------------------------------------------------------------------
 *
 * Browser tərəfindən rahat decode edilən raster şəkillər:
 * PNG/JPG/JPEG/WEBP/AVIF/BMP -> JPEG-ə sıxılır.
 *
 * GIF -> animasiyanı itirməmək üçün original saxlanılır.
 * SVG -> vektor olduğu üçün original saxlanılır.
 * HEIC/HEIF/TIFF -> bəzi browser-lər decode etmir; decode alınmasa
 * original saxlanılır. Beləliklə upload bloklanmır.
 */

const COMPRESSION_MAX_WIDTH = 1920;
const COMPRESSION_MAX_HEIGHT = 1920;
const COMPRESSION_QUALITY = 0.82;

const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes)) return "0 MB";

  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(2)} MB`;
};

const getFileExtension = (file) => {
  const name = String(file?.name || "").toLowerCase();
  const dotIndex = name.lastIndexOf(".");
  return dotIndex >= 0 ? name.slice(dotIndex) : "";
};

const isNeverCompressExtension = (file) => {
  const ext = getFileExtension(file);

  return [
    ".gif",
    ".svg",
    ".heic",
    ".heif",
    ".tiff",
    ".tif",
  ].includes(ext);
};

const loadImageForCompression = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(
        new Error(
          "Browser şəkli decode edə bilmədi."
        )
      );
    };

    image.src = objectUrl;
  });

const compressImageFile = async (file) => {
  if (
    !file ||
    isNeverCompressExtension(file)
  ) {
    return file;
  }

  try {
    let source;

    // createImageBitmap varsa əvvəl onu yoxlayırıq.
    if (
      typeof createImageBitmap ===
      "function"
    ) {
      try {
        source =
          await createImageBitmap(
            file,
            {
              imageOrientation:
                "from-image",
            }
          );
      } catch (error) {
        source =
          await loadImageForCompression(
            file
          );
      }
    } else {
      source =
        await loadImageForCompression(
          file
        );
    }

    const sourceWidth =
      source.width;

    const sourceHeight =
      source.height;

    if (
      !sourceWidth ||
      !sourceHeight
    ) {
      if (source?.close) {
        source.close();
      }

      return file;
    }

    const scale = Math.min(
      1,
      COMPRESSION_MAX_WIDTH /
        sourceWidth,
      COMPRESSION_MAX_HEIGHT /
        sourceHeight
    );

    const width = Math.max(
      1,
      Math.round(
        sourceWidth * scale
      )
    );

    const height = Math.max(
      1,
      Math.round(
        sourceHeight * scale
      )
    );

    // Artıq kiçikdirsə belə JPEG keyfiyyətini azaltmaqla ölçünü optimallaşdırırıq.
    const canvas =
      document.createElement(
        "canvas"
      );

    canvas.width = width;
    canvas.height = height;

    // PNG/SVG kimi şəffaflıq daşıyan şəkillər üçün alpha saxlanılır.
    const ctx =
      canvas.getContext(
        "2d",
        {
          alpha: true,
        }
      );

    if (!ctx) {
      if (source?.close) {
        source.close();
      }

      return file;
    }

    ctx.drawImage(
      source,
      0,
      0,
      width,
      height
    );

    if (source?.close) {
      source.close();
    }

    const originalExtension =
      getFileExtension(file);

    // Şəffaf PNG-ləri JPEG-ə çevirib şəffaflığı itirmirik.
    // WebP daha kiçik ölçü verir və alpha kanalını qoruyur.
    const outputType =
      originalExtension ===
        ".png" ||
      originalExtension ===
        ".webp"
        ? "image/webp"
        : "image/jpeg";

    const blob =
      await new Promise(
        (resolve) => {
          canvas.toBlob(
            resolve,
            outputType,
            COMPRESSION_QUALITY
          );
        }
      );

    if (!blob) {
      return file;
    }

    // Compression nəticəsi originaldan böyükdürsə originalı saxlayırıq.
    if (
      blob.size >= file.size
    ) {
      return file;
    }

    const originalName =
      String(
        file.name || "sekil"
      );

    const baseName =
      originalName.replace(
        /\.[^/.]+$/,
        ""
      );

    const outputExtension =
      outputType ===
      "image/webp"
        ? ".webp"
        : ".jpg";

    return new File(
      [blob],
      `${baseName}${outputExtension}`,
      {
        type: outputType,
        lastModified:
          Date.now(),
      }
    );
  } catch (error) {
    // HEIC/HEIF/TIFF və browser-in decode etmədiyi digər formatlar
    // upload-dan çıxarılmır, original fayl göndərilir.
    console.warn(
      `Şəkil sıxışdırılmadı: ${
        file?.name ||
        "naməlum"
      }`,
      error
    );

    return file;
  }
};

const createSafePreview = async (
  file,
  index
) => {
  let previewUrl = null;

  try {
    // Browser həmin formatı göstərə bilirsə normal blob preview.
    previewUrl =
      URL.createObjectURL(
        file
      );

    await new Promise(
      (
        resolve,
        reject
      ) => {
        const img =
          new Image();

        img.onload = () =>
          resolve();

        img.onerror = () =>
          reject(
            new Error(
              "Preview dəstəklənmir"
            )
          );

        img.src =
          previewUrl;
      }
    );

    return previewUrl;
  } catch (error) {
    // Image decode uğursuz olduqda blob URL-ni təmizlə.
    try {
      if (previewUrl) {
        URL.revokeObjectURL(
          previewUrl
        );
      }
    } catch (_) {}

    // HEIC/HEIF/TIFF kimi formatlar üçün sadə fallback kartı.
    const extension =
      getFileExtension(file)
        .replace(
          ".",
          ""
        )
        .toUpperCase() ||
      "IMAGE";

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="600" height="600" viewBox="0 0 600 600">
        <rect width="600" height="600" fill="#e2e8f0"/>
        <circle cx="300" cy="250" r="90" fill="#94a3b8"/>
        <path d="M190 410l75-85 55 60 40-45 50 70H190z" fill="#64748b"/>
        <text x="300" y="500" text-anchor="middle" font-family="Arial, sans-serif" font-size="42" font-weight="700" fill="#334155">${extension}</text>
        <text x="300" y="545" text-anchor="middle" font-family="Arial, sans-serif" font-size="22" fill="#475569">Önizləmə dəstəklənmir</text>
      </svg>
    `)}`;
  }
};

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "";


/*
|--------------------------------------------------------------------------
| Kateqoriyalar
|--------------------------------------------------------------------------
*/

const categories = [
  "Elektronika",
  "Telefon",
  "Kompüter",
  "Məişət texnikası",
  "Mebel",
  "Avtomobil",
  "Geyim",
  "Ayaqqabı",
  "Uşaq məhsulları",
  "İdman",
  "Hobbi",
  "Kitab",
  "Digər",
];


/*
|--------------------------------------------------------------------------
| Məhsulun vəziyyəti
|--------------------------------------------------------------------------
*/

const conditions = [
  "Yeni",
  "Əla vəziyyətdə",
  "Yaxşı vəziyyətdə",
  "İşlənmiş",
  "Təmirə ehtiyacı var",
];


/*
|--------------------------------------------------------------------------
| Çatdırılma seçimləri
|--------------------------------------------------------------------------
*/

const deliveryOptions = [
  "Satıcıdan götürmə",
  "Çatdırılma mümkündür",
  "Hər ikisi",
];


const Addpagenation = () => {
  const navigate =
    useNavigate();

  const dispatch =
    useDispatch();

  const fileInputRef =
    useRef(null);


  /*
  |--------------------------------------------------------------------------
  | Redux auth
  |--------------------------------------------------------------------------
  */

  const userInfo =
    useSelector(
      (state) =>
        state.auth?.userInfo
    );


  /*
  |--------------------------------------------------------------------------
  | RTK Mutation
  |--------------------------------------------------------------------------
  */

  const [
    addQolbaqJson,
    {
      isLoading: creating,
    },
  ] =
    useAddQolbaqJsonMutation();


  /*
  |--------------------------------------------------------------------------
  | State
  |--------------------------------------------------------------------------
  */

  const [
    checkingUser,
    setCheckingUser,
  ] = useState(true);


  /*
   * Qeydiyyatsız istifadəçi üçün modal
   */

  const [
    showRegisterPrompt,
    setShowRegisterPrompt,
  ] = useState(false);

  const [
    uploading,
    setUploading,
  ] = useState(false);

  const [
    title,
    setTitle,
  ] = useState("");

  const [
    description,
    setDescription,
  ] = useState("");

  const [
    category,
    setCategory,
  ] = useState("");

  const [
    condition,
    setCondition,
  ] = useState(
    "İşlənmiş"
  );

  const [
    brand,
    setBrand,
  ] = useState("");

  const [
    model,
    setModel,
  ] = useState("");

  const [
    price,
    setPrice,
  ] = useState("");

  const [
    phone,
    setPhone,
  ] = useState("");

  const [
    location,
    setLocation,
  ] = useState("");

  const [
    distance,
    setDistance,
  ] = useState("");

  const [
    delivery,
    setDelivery,
  ] = useState(
    "Satıcıdan götürmə"
  );

  const [
    negotiable,
    setNegotiable,
  ] = useState(false);

  const [
    warranty,
    setWarranty,
  ] = useState("");

  const [
    stock,
    setStock,
  ] = useState("1");

  const [
    photos,
    setPhotos,
  ] = useState([]);

  const [
    previews,
    setPreviews,
  ] = useState([]);


  /*
  |--------------------------------------------------------------------------
  | Auth yoxlaması
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    /*
     * userInfo undefined-dirsə,
     * auth məlumatları hələ yüklənir.
     */

    if (
      userInfo ===
      undefined
    ) {
      return;
    }

    setCheckingUser(
      false
    );

    /*
     * Əvvəlki kimi avtomatik register səhifəsinə
     * göndərmirik.
     *
     * İstifadəçiyə modal göstəririk.
     */

    if (!userInfo) {
      setShowRegisterPrompt(
        true
      );
    }
  }, [
    userInfo,
  ]);


  /*
  |--------------------------------------------------------------------------
  | Preview URL-lərinin təmizlənməsi
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    return () => {
      previews.forEach(
        (url) => {
          if (
            url.startsWith(
              "blob:"
            )
          ) {
            URL.revokeObjectURL(
              url
            );
          }
        }
      );
    };
  }, [previews]);


  /*
  |--------------------------------------------------------------------------
  | Pulsuz elan hesablaması
  |--------------------------------------------------------------------------
  */

  const isPaidUser =
    userInfo?.payment ===
    true;

  const usedFreeAds =
    Number(
      userInfo?.freeAdCount ||
        0
    );

  const remainingFreeAds =
    Math.max(
      0,
      MAX_FREE_ADS -
        usedFreeAds
    );

  const freeAdLimitReached =
    !isPaidUser &&
    usedFreeAds >=
      MAX_FREE_ADS;


  /*
  |--------------------------------------------------------------------------
  | Telefon nömrəsinin formatlanması
  |--------------------------------------------------------------------------
  */

  const formatPhone = (
    value
  ) => {
    let digits =
      value.replace(
        /\D/g,
        ""
      );

    /*
     * Azərbaycan mobil telefon formatı
     */

    if (
      digits.startsWith(
        "994"
      )
    ) {
      digits =
        digits.substring(3);
    }

    if (
      digits.startsWith(
        "0"
      )
    ) {
      digits =
        digits.substring(1);
    }

    digits =
      digits.substring(
        0,
        9
      );

    let result = "";

    if (
      digits.length > 0
    ) {
      result +=
        "0" +
        digits.substring(
          0,
          2
        );
    }

    if (
      digits.length >= 2
    ) {
      result +=
        "-" +
        digits.substring(
          2,
          5
        );
    }

    if (
      digits.length >= 5
    ) {
      result +=
        "-" +
        digits.substring(
          5,
          7
        );
    }

    if (
      digits.length >= 7
    ) {
      result +=
        "-" +
        digits.substring(
          7,
          9
        );
    }

    return result;
  };


  /*
  |--------------------------------------------------------------------------
  | Şəkillərin seçilməsi
  |--------------------------------------------------------------------------
  */

  const handlePhotoChange =
    async (
      event
    ) => {
      const files =
        Array.from(
          event.target
            .files || []
        );

      if (!files.length) {
        return;
      }

      const available =
        MAX_IMAGES -
        photos.length;

      if (available <= 0) {
        toast.warning(
          `Maksimum ${MAX_IMAGES} şəkil əlavə edə bilərsiniz.`
        );

        event.target.value =
          "";

        return;
      }

      const selectedFiles =
        files.slice(
          0,
          available
        );

      if (
        files.length >
        available
      ) {
        toast.info(
          `Maksimum ${MAX_IMAGES} şəkil əlavə etmək mümkündür.`
        );
      }

      /*
       * Format yoxlaması. MIME type boş olsa belə extension ilə
       * HEIC/HEIF/AVIF/TIFF/SVG kimi formatları qəbul edirik.
       */

      const validFiles =
        selectedFiles.filter(
          (file) =>
            isSupportedImageFile(
              file
            )
        );

      if (
        validFiles.length !==
        selectedFiles.length
      ) {
        toast.warning(
          "Dəstəklənməyən fayllar siyahıdan çıxarıldı."
        );
      }

      /*
       * İlkin fayl ölçüsü limiti: 10 MB.
       * Compression bundan sonra işləyir.
       */

      const maxSize =
        10 * 1024 * 1024;

      const sizeValidFiles =
        validFiles.filter(
          (file) =>
            file.size <=
            maxSize
        );

      if (
        sizeValidFiles.length !==
        validFiles.length
      ) {
        toast.warning(
          "10 MB-dan böyük şəkillər qəbul edilmir."
        );
      }

      if (
        !sizeValidFiles.length
      ) {
        event.target.value =
          "";

        return;
      }

      /*
       * Compression — fayllar ardıcıl sıxışdırılır.
       * Mobil cihazlarda Promise.all əvəzinə bu üsul RAM istifadəsini
       * aşağı saxlayır.
       */

      setUploading(true);

      try {
        const compressedFiles =
          [];

        let compressedCount =
          0;

        let unchangedCount =
          0;

        let totalOriginalBytes =
          0;

        let totalCompressedBytes =
          0;

        console.groupCollapsed(
          `📦 Şəkil compression başladı — ${sizeValidFiles.length} şəkil`
        );

        for (
          const file of sizeValidFiles
        ) {
          const originalSize =
            file.size;

          const compressed =
            await compressImageFile(
              file
            );

          const compressedSize =
            compressed.size;

          const savedBytes =
            Math.max(
              0,
              originalSize -
                compressedSize
            );

          const savedPercent =
            originalSize > 0
              ? (
                  (savedBytes /
                    originalSize) *
                  100
                )
              : 0;

          totalOriginalBytes +=
            originalSize;

          totalCompressedBytes +=
            compressedSize;

          console.group(
            `🖼️ ${file.name}`
          );

          console.log(
            `📦 Original ölçü: ${formatFileSize(
              originalSize
            )}`
          );

          console.log(
            `📉 Son ölçü: ${formatFileSize(
              compressedSize
            )}`
          );

          if (
            compressed !== file
          ) {
            console.log(
              `✅ Sıxışdırıldı: ${formatFileSize(
                savedBytes
              )} qənaət (${savedPercent.toFixed(
                1
              )}%)`
            );

            console.log(
              `🔄 Format: ${
                file.type ||
                "naməlum"
              } → ${
                compressed.type ||
                "naməlum"
              }`
            );

            compressedCount +=
              1;
          } else {
            console.log(
              `ℹ️ Original saxlanıldı — browser bu formatı sıxışdıra bilmədi və ya nəticə daha böyük olardı.`
            );

            unchangedCount +=
              1;
          }

          console.groupEnd();

          compressedFiles.push(
            compressed
          );
        }

        const totalSavedBytes =
          Math.max(
            0,
            totalOriginalBytes -
              totalCompressedBytes
          );

        const totalSavedPercent =
          totalOriginalBytes >
          0
            ? (
                (totalSavedBytes /
                  totalOriginalBytes) *
                100
              )
            : 0;

        console.log(
          `📊 ÜMUMİ: ${formatFileSize(
            totalOriginalBytes
          )} → ${formatFileSize(
            totalCompressedBytes
          )}`
        );

        console.log(
          `💾 Ümumi qənaət: ${formatFileSize(
            totalSavedBytes
          )} (${totalSavedPercent.toFixed(
            1
          )}%)`
        );

        console.log(
          `✅ Sıxışdırılan: ${compressedCount} | ℹ️ Original qalan: ${unchangedCount}`
        );

        console.groupEnd();

        const newPreviews =
          [];

        for (
          let i = 0;
          i <
          compressedFiles.length;
          i += 1
        ) {
          const preview =
            await createSafePreview(
              compressedFiles[i],
              photos.length +
                i
            );

          newPreviews.push(
            preview
          );
        }

        setPhotos(
          (prev) => [
            ...prev,
            ...compressedFiles,
          ]
        );

        setPreviews(
          (prev) => [
            ...prev,
            ...newPreviews,
          ]
        );
      } catch (error) {
        console.error(
          "Şəkil compression xətası:",
          error
        );

        toast.error(
          "Şəkillərin hazırlanması zamanı xəta baş verdi."
        );
      } finally {
        setUploading(false);

        event.target.value =
          "";
      }
    };


  /*
  |--------------------------------------------------------------------------
  | Şəklin silinməsi
  |--------------------------------------------------------------------------
  */

  const removePhoto = (
    index
  ) => {
    setPhotos(
      (prev) =>
        prev.filter(
          (_, i) =>
            i !== index
        )
    );

    setPreviews(
      (prev) => {
        const url =
          prev[index];

        if (
          url &&
          url.startsWith(
            "blob:"
          )
        ) {
          URL.revokeObjectURL(
            url
          );
        }

        return prev.filter(
          (_, i) =>
            i !== index
        );
      }
    );
  };


  /*
  |--------------------------------------------------------------------------
  | Formun yoxlanılması
  |--------------------------------------------------------------------------
  */

  const validateForm =
    () => {
      if (
        !title.trim()
      ) {
        toast.error(
          "Elan başlığını daxil edin."
        );

        return false;
      }

      if (
        title.trim()
          .length < 5
      ) {
        toast.error(
          "Elan başlığı ən azı 5 simvoldan ibarət olmalıdır."
        );

        return false;
      }

      if (!category) {
        toast.error(
          "Kateqoriya seçin."
        );

        return false;
      }

      if (
        !price ||
        Number.isNaN(
          Number(price)
        ) ||
        Number(price) < 0
      ) {
        toast.error(
          "Düzgün qiymət daxil edin."
        );

        return false;
      }

      if (
        !phone ||
        phone.replace(
          /\D/g,
          ""
        ).length < 9
      ) {
        toast.error(
          "Düzgün telefon nömrəsi daxil edin."
        );

        return false;
      }

      if (
        !photos.length
      ) {
        toast.error(
          "Ən azı bir şəkil əlavə edin."
        );

        return false;
      }

      if (
        photos.length >
        MAX_IMAGES
      ) {
        toast.error(
          `Maksimum ${MAX_IMAGES} şəkil əlavə edə bilərsiniz.`
        );

        return false;
      }

      if (
        !stock ||
        Number(stock) < 1
      ) {
        toast.error(
          "Mövcud say ən azı 1 olmalıdır."
        );

        return false;
      }

      return true;
    };


  /*
  |--------------------------------------------------------------------------
  | Elanın göndərilməsi
  |--------------------------------------------------------------------------
  */

  const handleSubmit =
    async (
      event
    ) => {
      event.preventDefault();

      /*
       * Auth hələ hazır deyilsə
       */

      if (
        checkingUser
      ) {
        return;
      }

      /*
       * İstifadəçi daxil olmayıbsa
       *
       * Artıq birbaşa /register etmirik.
       * Modal açılır.
       */

      if (!userInfo) {
        setShowRegisterPrompt(
          true
        );

        return;
      }

      /*
       * Pulsuz elan limiti
       */

      if (
        freeAdLimitReached
      ) {
        toast.error(
          "Pulsuz elan yerləşdirmə haqqınız bitib."
        );

        return;
      }

      /*
       * Formun yoxlanılması
       */

      if (
        !validateForm()
      ) {
        return;
      }

      try {
        /*
        |--------------------------------------------------------------------------
        | ADDIM 1 — Şəkillərin yüklənməsi
        |--------------------------------------------------------------------------
        */

        setUploading(true);

        const formData =
          new FormData();

        photos.forEach(
          (file) => {
            formData.append(
              "photo",
              file
            );
          }
        );

        const uploadRes =
          await axios.post(
            `${API_BASE_URL}/api/product/upload-image`,
            formData,
            {
              withCredentials:
                true,

              headers: {
                "Content-Type":
                  "multipart/form-data",
              },
            }
          );

        const imageUrls =
          uploadRes.data
            ?.imageUrls ||
          uploadRes.data
            ?.urls ||
          [];

        if (
          !Array.isArray(
            imageUrls
          ) ||
          !imageUrls.length
        ) {
          throw new Error(
            "Şəkillər serverə yüklənmədi."
          );
        }


        /*
        |--------------------------------------------------------------------------
        | ADDIM 2 — Elanın yaradılması
        |--------------------------------------------------------------------------
        */

        const productData =
          {
            /*
             * userId burada göndərilmir.
             *
             * Backend req.user._id istifadə edir.
             */

            title:
              title.trim(),

            description:
              description.trim(),

            category:
              category.trim(),

            condition,

            brand:
              brand.trim(),

            model:
              model.trim(),

            phone:
              phone.trim(),

            location:
              location.trim(),

            distance:
              distance.trim(),

            delivery,

            price:
              Number(price),

            negotiable,

            warranty:
              warranty.trim(),

            stock:
              Number(stock),

            photo:
              imageUrls,
          };


        /*
        |--------------------------------------------------------------------------
        | ADDIM 3 — API sorğusu
        |--------------------------------------------------------------------------
        */

        const result =
          await addQolbaqJson(
            productData
          ).unwrap();


        /*
        |--------------------------------------------------------------------------
        | Redux
        |--------------------------------------------------------------------------
        */

        if (
          result?.pagination
        ) {
          dispatch(
            addQolbaq(
              result.pagination
            )
          );
        }


        /*
        |--------------------------------------------------------------------------
        | Uğurlu nəticə
        |--------------------------------------------------------------------------
        */

        toast.success(
          "Elan uğurla dərc edildi! 🎉"
        );


        /*
         * Ana səhifəyə keç
         *
         * Əvvəlki səhv navigate sintaksisi
         * burada düzəldilib.
         */

        setTimeout(
          () => {
            navigate(
              "/",
              {
                replace:
                  true,
              }
            );

            /*
             * Ana səhifə render olduqdan sonra
             * məhsullar bölməsinə keç.
             */

            setTimeout(
              () => {
                const section =
                  document.getElementById(
                    "all-productsssssssssssss"
                  );

                if (section) {
                  section.scrollIntoView(
                    {
                      behavior:
                        "smooth",
                      block:
                        "start",
                    }
                  );
                }
              },
              300
            );
          },
          1000
        );
      } catch (error) {
        console.error(
          "❌ Elan əlavə edilərkən xəta baş verdi:",
          error
        );

        const message =
          error?.data
            ?.message ||
          error?.data?.error ||
          error?.response
            ?.data
            ?.message ||
          error?.response
            ?.data?.error ||
          error?.message ||
          "Elan yaradılarkən xəta baş verdi.";

        toast.error(
          message
        );
      } finally {
        setUploading(false);
      }
    };


  /*
  |--------------------------------------------------------------------------
  | Mövzu / Tema
  |--------------------------------------------------------------------------
  */

  const [theme, setTheme] =
    useState(
      localStorage.getItem(
        "theme"
      )
        ? localStorage.getItem(
            "theme"
          )
        : "light"
    );

  const element =
    document.documentElement;


  useEffect(() => {
    if (
      theme === "dark"
    ) {
      element.classList.add(
        "dark"
      );

      localStorage.setItem(
        "theme",
        "dark"
      );
    } else {
      element.classList.remove(
        "dark"
      );

      localStorage.setItem(
        "theme",
        "light"
      );
    }
  }, [theme]);


  /*
  |--------------------------------------------------------------------------
  | Auth yüklənməsi
  |--------------------------------------------------------------------------
  */

  if (
    checkingUser
  ) {
    return (
      <div
        className="
          min-h-screen
          flex items-center justify-center
          bg-slate-50 dark:bg-[#080a12]
          transition-colors duration-300
        "
      >
        <div className="flex flex-col items-center gap-4">

          <div
            className="
              w-14 h-14
              rounded-2xl
              flex items-center justify-center
              bg-blue-50 dark:bg-blue-500/10
            "
          >
            <Loader2
              className="
                w-8 h-8
                animate-spin
                text-blue-600
                dark:text-blue-400
              "
            />
          </div>

          <p className="text-slate-600 dark:text-slate-400 text-sm font-medium">
            Hesabınız yoxlanılır...
          </p>

        </div>
      </div>
    );
  }


  /*
  |--------------------------------------------------------------------------
  | Əsas interfeys
  |--------------------------------------------------------------------------
  */

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#080a12] pb-16 transition-colors duration-300">


      {/* ================================================================
          QEYDİYYAT MODALI
      ================================================================= */}

      {showRegisterPrompt && (
        <div
          className="
            fixed inset-0
            z-[10000]
            bg-black/60
            dark:bg-black/75
            backdrop-blur-md
            flex items-center justify-center
            px-4
            py-6
          "
          role="dialog"
          aria-modal="true"
          aria-labelledby="register-prompt-title"
        >

          <div
            className="
              w-full
              max-w-md
              max-h-[90vh]
              overflow-y-auto

              bg-white
              dark:bg-[#11141d]

              rounded-[28px]

              border
              border-slate-200
              dark:border-white/[0.08]

              shadow-2xl

              transition-colors
              duration-300
            "
          >

            {/* ICON */}

            <div className="pt-7 sm:pt-8 flex justify-center">

              <div
                className="
                  w-16 h-16
                  sm:w-20 sm:h-20

                  rounded-3xl

                  bg-blue-50
                  dark:bg-blue-500/10

                  border
                  border-blue-100
                  dark:border-blue-500/20

                  flex
                  items-center
                  justify-center

                  text-blue-600
                  dark:text-blue-400

                  shadow-sm
                "
              >

                <Package
                  size={34}
                  strokeWidth={1.8}
                />

              </div>

            </div>


            {/* CONTENT */}

            <div
              className="
                px-5
                sm:px-8

                pt-5
                pb-6

                text-center
              "
            >

              <h2
                id="register-prompt-title"
                className="
                  text-xl
                  sm:text-2xl

                  font-bold

                  text-slate-900
                  dark:text-white
                "
              >
                Elan yerləşdirmək üçün
                qeydiyyat tələb olunur
              </h2>


              <p
                className="
                  mt-3

                  text-sm
                  sm:text-[15px]

                  leading-6

                  text-slate-600
                  dark:text-slate-400
                "
              >
                Elanınızı yayımlamaq üçün
                əvvəlcə hesab yaratmalısınız.
                Qeydiyyatdan keçdikdən sonra
                məhsullarınızı əlavə edə,
                elanlarınızı idarə edə və
                alıcılarla əlaqə saxlaya
                bilərsiniz.
              </p>


              {/* INFO BOX */}

              <div
                className="
                  mt-5

                  rounded-2xl

                  bg-slate-50
                  dark:bg-white/[0.035]

                  border
                  border-slate-200
                  dark:border-white/[0.06]

                  p-4

                  text-left
                "
              >

                <div
                  className="
                    flex
                    items-start
                    gap-3
                  "
                >

                  <div
                    className="
                      mt-0.5

                      w-8
                      h-8

                      shrink-0

                      rounded-xl

                      bg-blue-100
                      dark:bg-blue-500/10

                      text-blue-600
                      dark:text-blue-400

                      flex
                      items-center
                      justify-center
                    "
                  >

                    <ShieldCheck
                      size={17}
                    />

                  </div>


                  <div>

                    <p
                      className="
                        text-sm
                        font-semibold

                        text-slate-800
                        dark:text-slate-200
                      "
                    >
                      Hesabınız niyə lazımdır?
                    </p>


                    <p
                      className="
                        mt-1

                        text-xs
                        sm:text-sm

                        leading-5

                        text-slate-500
                        dark:text-slate-400
                      "
                    >
                      Elanın sizə aid olduğunu
                      təsdiqləmək və sonradan
                      elanlarınızı idarə edə
                      bilməyiniz üçün hesab
                      tələb olunur.
                    </p>

                  </div>

                </div>

              </div>


              {/* BUTTONS */}

              <div
                className="
                  mt-6
                  space-y-3
                "
              >

                {/* REGISTER */}

                <button
                  type="button"
                  onClick={() => {
                    setShowRegisterPrompt(
                      false
                    );

                    navigate(
                      "/register",
                      {
                        replace:
                          true,
                      }
                    );
                  }}
                  className="
                    w-full
                    min-h-[52px]

                    rounded-2xl

                    bg-blue-600
                    hover:bg-blue-700

                    active:scale-[0.98]

                    text-white

                    font-bold

                    text-sm
                    sm:text-base

                    shadow-lg
                    shadow-blue-600/20

                    transition-all
                    duration-200

                    flex
                    items-center
                    justify-center
                    gap-2
                  "
                >

                  Qeydiyyatdan keç

                  <ArrowLeft
                    size={18}
                    className="rotate-180"
                  />

                </button>


                {/* STAY */}

                <button
                  type="button"
                  onClick={() => {
                    setShowRegisterPrompt(
                      false
                    );
                  }}
                  className="
                    w-full
                    min-h-[50px]

                    rounded-2xl

                    bg-slate-100
                    hover:bg-slate-200

                    dark:bg-white/[0.05]
                    dark:hover:bg-white/[0.08]

                    border
                    border-slate-200
                    dark:border-white/[0.07]

                    text-slate-700
                    dark:text-slate-300

                    font-semibold

                    text-sm

                    transition-all
                    duration-200
                  "
                >

                  Xeyr, bu səhifədə
                  qalmaq istəyirəm

                </button>

              </div>


              {/* FOOTER */}

              <p
                className="
                  mt-4

                  text-[11px]
                  sm:text-xs

                  leading-5

                  text-slate-400
                  dark:text-slate-500
                "
              >
                Qeydiyyatdan keçmək
                istəmirsinizsə, bu pəncərəni
                bağlayaraq elan yerləşdirmə
                səhifəsində qala bilərsiniz.
              </p>

            </div>

          </div>

        </div>
      )}


      {/* ================================================================
          YÜKLƏNMƏ PƏNCƏRƏSİ
      ================================================================= */}

      {(uploading ||
        creating) && (
        <div
          className="
            fixed inset-0
            z-[9999]

            bg-black/50
            dark:bg-black/70

            backdrop-blur-sm

            flex
            items-center
            justify-center

            px-4
          "
        >

          <div
            className="
              w-full
              max-w-sm

              bg-white
              dark:bg-[#11141d]

              border
              border-slate-200
              dark:border-white/[0.08]

              rounded-3xl

              shadow-2xl

              p-8

              text-center

              transition-colors
              duration-300
            "
          >

            <div
              className="
                w-16
                h-16
                mx-auto
                mb-5

                rounded-full

                bg-blue-50
                dark:bg-blue-500/10

                flex
                items-center
                justify-center
              "
            >

              <Loader2
                className="
                  w-8
                  h-8

                  text-blue-600
                  dark:text-blue-400

                  animate-spin
                "
              />

            </div>


            <h3
              className="
                text-xl
                font-bold
                text-slate-900
                dark:text-white
              "
            >
              {uploading
                ? "Şəkillər yüklənir..."
                : "Elan dərc edilir..."}
            </h3>


            <p
              className="
                mt-2
                text-sm
                text-slate-500
                dark:text-slate-400
              "
            >
              Zəhmət olmasa, səhifəni
              bağlamayın.
            </p>

          </div>

        </div>
      )}


      {/* ================================================================
          BAŞLIQ
      ================================================================= */}

      <div
        className="
          sticky top-0 z-40
          bg-white/90 dark:bg-[#080a12]/90
          backdrop-blur
          border-b
          border-slate-200 dark:border-white/[0.07]
          transition-colors duration-300
        "
      >

        <div
          className="
            max-w-5xl
            mx-auto
            px-4
            sm:px-6
          "
        >

          <div
            className="
              h-16
              flex
              items-center
              justify-between
            "
          >

            <button
              type="button"
              onClick={() =>
                navigate("/")
              }
              className="
                inline-flex
                items-center
                gap-2

                text-slate-600
                dark:text-slate-400

                hover:text-slate-900
                dark:hover:text-white

                transition
              "
            >

              <ArrowLeft
                size={20}
              />

              <span className="hidden sm:inline">
                Geri
              </span>

            </button>


            <div className="text-center">

              <h1
                className="
                  text-lg
                  sm:text-xl
                  font-bold
                  text-slate-900
                  dark:text-white
                "
              >
                Yeni elan
              </h1>


              <p
                className="
                  hidden sm:block
                  text-xs
                  text-slate-500
                  dark:text-slate-400
                "
              >
                Məhsulunuzu satışa çıxarın
              </p>

            </div>


            <div className="w-12" />

          </div>

        </div>

      </div>


      {/* ================================================================
          PULSUZ ELAN HAQQI
          DAİM EKRANDA GÖRÜNÜR
      ================================================================= */}

      {!isPaidUser && (
        <div
          className="
            max-w-5xl
            mx-auto

            px-4
            sm:px-6

            pt-5
          "
        >

          <div
            className={`
              relative
              overflow-hidden

              rounded-3xl

              border

              p-5
              sm:p-6

              shadow-sm

              transition-all
              duration-300

              ${
                freeAdLimitReached
                  ? `
                    border-red-200
                    bg-red-50

                    dark:border-red-500/20
                    dark:bg-red-500/[0.07]
                  `
                  : `
                    border-blue-200
                    bg-blue-50

                    dark:border-blue-500/20
                    dark:bg-blue-500/[0.07]
                  `
              }
            `}
          >

            {/* Dekorativ dairə */}

            <div
              className={`
                absolute
                -right-10
                -top-10

                w-32
                h-32

                rounded-full

                blur-3xl

                pointer-events-none

                ${
                  freeAdLimitReached
                    ? "bg-red-400/20"
                    : "bg-blue-400/20"
                }
              `}
            />


            <div
              className="
                relative

                flex
                flex-col
                sm:flex-row

                sm:items-center
                sm:justify-between

                gap-5
              "
            >

              {/* SOL TƏRƏF */}

              <div
                className="
                  flex
                  items-center
                  gap-4
                "
              >

                <div
                  className={`
                    w-12
                    h-12

                    shrink-0

                    rounded-2xl

                    flex
                    items-center
                    justify-center

                    ${
                      freeAdLimitReached
                        ? `
                          bg-red-100
                          text-red-600

                          dark:bg-red-500/10
                          dark:text-red-400
                        `
                        : `
                          bg-blue-100
                          text-blue-600

                          dark:bg-blue-500/10
                          dark:text-blue-400
                        `
                    }
                  `}
                >

                  {freeAdLimitReached ? (
                    <X
                      size={25}
                      strokeWidth={
                        2.5
                      }
                    />
                  ) : (
                    <CheckCircle2
                      size={25}
                      strokeWidth={
                        2
                      }
                    />
                  )}

                </div>


                <div>

                  <p
                    className={`
                      text-base
                      sm:text-lg

                      font-bold

                      ${
                        freeAdLimitReached
                          ? `
                            text-red-700
                            dark:text-red-400
                          `
                          : `
                            text-blue-800
                            dark:text-blue-300
                          `
                      }
                    `}
                  >
                    Pulsuz elan haqqınız
                  </p>


                  {freeAdLimitReached ? (
                    <p
                      className="
                        mt-1

                        text-sm

                        text-red-600
                        dark:text-red-400
                      "
                    >
                      3 pulsuz elanınızın hamısından istifadə etmisiniz.
                    </p>
                  ) : (
                    <p
                      className="
                        mt-1

                        text-sm

                        text-slate-600
                        dark:text-slate-400
                      "
                    >
                      Daha{" "}
                      <strong
                        className="
                          font-bold
                          text-blue-600
                          dark:text-blue-400
                        "
                      >
                        {remainingFreeAds}
                      </strong>{" "}
                      pulsuz elan yerləşdirə bilərsiniz.
                    </p>
                  )}

                </div>

              </div>


              {/* SAĞ TƏRƏF — SAY */}

              <div
                className="
                  flex
                  items-center
                  gap-4
                "
              >

                {/* PROGRESS */}

                <div
                  className="
                    hidden
                    sm:flex

                    items-center
                    gap-2
                  "
                >

                  {Array.from({
                    length:
                      MAX_FREE_ADS,
                  }).map(
                    (
                      _,
                      index
                    ) => {

                      const active =
                        index <
                        remainingFreeAds;

                      return (
                        <div
                          key={
                            index
                          }
                          className={`
                            w-10
                            h-2

                            rounded-full

                            transition-all
                            duration-300

                            ${
                              active
                                ? freeAdLimitReached
                                  ? "bg-red-500"
                                  : "bg-blue-500"
                                : `
                                    bg-slate-200
                                    dark:bg-white/10
                                  `
                            }
                          `}
                        />
                      );
                    }
                  )}

                </div>


                {/* RƏQƏM */}

                <div
                  className={`
                    flex
                    items-center
                    justify-center

                    w-16
                    h-16

                    shrink-0

                    rounded-2xl

                    font-black
                    text-2xl

                    border

                    ${
                      freeAdLimitReached
                        ? `
                          bg-red-100
                          text-red-600
                          border-red-200

                          dark:bg-red-500/10
                          dark:text-red-400
                          dark:border-red-500/20
                        `
                        : `
                          bg-blue-600
                          text-white
                          border-blue-600

                          dark:bg-blue-600
                          dark:text-white
                        `
                    }
                  `}
                >
                  {remainingFreeAds}
                </div>

              </div>

            </div>


            {/* MOBİL PROGRESS */}

            <div
              className="
                relative

                flex
                sm:hidden

                gap-2

                mt-5
              "
            >

              {Array.from({
                length:
                  MAX_FREE_ADS,
              }).map(
                (
                  _,
                  index
                ) => {

                  const active =
                    index <
                    remainingFreeAds;

                  return (
                    <div
                      key={
                        index
                      }
                      className={`
                        h-2
                        flex-1

                        rounded-full

                        transition-all
                        duration-300

                        ${
                          active
                            ? freeAdLimitReached
                              ? "bg-red-500"
                              : "bg-blue-500"
                            : `
                                bg-slate-200
                                dark:bg-white/10
                              `
                        }
                      `}
                    />
                  );
                }
              )}

            </div>


            {/* AŞAĞI MƏLUMAT */}

            <div
              className="
                relative

                mt-3

                flex
                items-center
                justify-between

                text-xs

                text-slate-500
                dark:text-slate-400
              "
            >

              <span>
                İstifadə olunan:{" "}
                <strong>
                  {Math.min(
                    usedFreeAds,
                    MAX_FREE_ADS
                  )}
                </strong>
              </span>

              <span>
                Ümumi:{" "}
                <strong>
                  {MAX_FREE_ADS}
                </strong>{" "}
                pulsuz elan
              </span>

            </div>

          </div>

        </div>
      )}


      {/* ================================================================
          FORM
      ================================================================= */}

      <form
        onSubmit={
          handleSubmit
        }
        className="
          max-w-5xl
          mx-auto
          px-4
          sm:px-6
          pt-6
        "
      >


        {/* ==============================================================
            ŞƏKİLLƏR
        ============================================================== */}

        <section
          className="
            bg-white
            dark:bg-[#11141d]

            rounded-3xl

            border
            border-slate-200
            dark:border-white/[0.07]

            shadow-sm
            dark:shadow-black/20

            p-4
            sm:p-6

            mb-5

            transition-colors
            duration-300
          "
        >

          <div
            className="
              flex
              items-start
              justify-between
              gap-4
              mb-5
            "
          >

            <div>

              <h2
                className="
                  text-lg
                  font-bold
                  text-slate-900
                  dark:text-white
                "
              >
                Məhsulun şəkilləri
              </h2>


              <p
                className="
                  text-sm
                  text-slate-500
                  dark:text-slate-400
                  mt-1
                "
              >
                İlk şəkil əsas şəkil kimi
                göstəriləcək.
              </p>

            </div>


            <span
              className="
                text-sm
                font-medium
                text-slate-500
                dark:text-slate-400
                whitespace-nowrap
              "
            >
              {photos.length}/
              {MAX_IMAGES}
            </span>

          </div>


          <div
            className="
              grid
              grid-cols-2
              sm:grid-cols-4
              lg:grid-cols-5
              gap-3
            "
          >

            {previews.map(
              (
                preview,
                index
              ) => (

                <div
                  key={
                    preview
                  }
                  className="
                    relative
                    aspect-square

                    rounded-2xl
                    overflow-hidden

                    bg-slate-100
                    dark:bg-slate-800

                    border
                    border-slate-200
                    dark:border-white/[0.08]

                    group
                  "
                >

                  <img
                    src={
                      preview
                    }
                    alt={`Məhsul ${
                      index + 1
                    }`}
                    className="
                      w-full
                      h-full
                      object-cover
                    "
                  />


                  {index ===
                    0 && (
                    <div
                      className="
                        absolute
                        left-2
                        bottom-2

                        px-2
                        py-1

                        rounded-lg

                        bg-black/70

                        text-white

                        text-[11px]

                        font-medium

                        backdrop-blur-sm
                      "
                    >
                      Əsas şəkil
                    </div>
                  )}


                  <button
                    type="button"
                    onClick={() =>
                      removePhoto(
                        index
                      )
                    }
                    className="
                      absolute
                      top-2
                      right-2

                      w-8
                      h-8

                      rounded-full

                      bg-black/70

                      text-white

                      flex
                      items-center
                      justify-center

                      hover:bg-red-600

                      transition
                    "
                  >

                    <X
                      size={16}
                    />

                  </button>

                </div>

              )
            )}


            {photos.length <
              MAX_IMAGES && (
              <button
                type="button"
                onClick={() =>
                  fileInputRef.current?.click()
                }
                className="
                  aspect-square

                  rounded-2xl

                  border-2
                  border-dashed

                  border-slate-300
                  dark:border-slate-700

                  hover:border-blue-500

                  hover:bg-blue-50/50
                  dark:hover:bg-blue-500/10

                  transition

                  flex
                  flex-col

                  items-center
                  justify-center

                  gap-2

                  text-slate-500
                  dark:text-slate-400

                  hover:text-blue-600
                  dark:hover:text-blue-400
                "
              >

                <ImagePlus
                  size={28}
                />

                <span
                  className="
                    text-xs
                    font-medium
                  "
                >
                  Şəkil əlavə et
                </span>

              </button>
            )}

          </div>


          <input
            ref={
              fileInputRef
            }
            type="file"
            accept=".png,.jpg,.jpeg,.gif,.webp,.heic,.heif,.avif,.bmp,.tiff,.tif,.svg,image/*"
            multiple
            className="hidden"
            onChange={
              handlePhotoChange
            }
          />

        </section>


        {/* ==============================================================
            ƏSAS MƏLUMATLAR
        ============================================================== */}

        <section
          className="
            bg-white
            dark:bg-[#11141d]

            rounded-3xl

            border
            border-slate-200
            dark:border-white/[0.07]

            shadow-sm
            dark:shadow-black/20

            p-4
            sm:p-6

            mb-5

            transition-colors
            duration-300
          "
        >

          <div
            className="
              flex
              items-center
              gap-3
              mb-6
            "
          >

            <div
              className="
                w-10
                h-10

                rounded-xl

                bg-blue-50
                dark:bg-blue-500/10

                text-blue-600
                dark:text-blue-400

                flex
                items-center
                justify-center
              "
            >

              <Tag
                size={20}
              />

            </div>


            <div>

              <h2
                className="
                  text-lg
                  font-bold
                  text-slate-900
                  dark:text-white
                "
              >
                Məhsul barədə
              </h2>


              <p
                className="
                  text-sm
                  text-slate-500
                  dark:text-slate-400
                "
              >
                Əsas məlumatları daxil edin
              </p>

            </div>

          </div>


          <div className="space-y-5">


            {/* ELAN BAŞLIĞI */}

            <div>

              <label
                className="
                  block
                  text-sm
                  font-semibold
                  text-slate-700
                  dark:text-slate-300
                  mb-2
                "
              >
                Elan başlığı *
              </label>


              <input
                type="text"
                value={title}
                onChange={(e) =>
                  setTitle(
                    e.target.value
                  )
                }
                placeholder="Məsələn: iPhone 15 Pro 256 GB"
                maxLength={100}
                className="
                  w-full
                  h-12
                  px-4

                  rounded-xl

                  border
                  border-slate-200
                  dark:border-white/[0.08]

                  bg-white
                  dark:bg-[#0c0f17]

                  text-slate-900
                  dark:text-white

                  placeholder:text-slate-400
                  dark:placeholder:text-slate-600

                  outline-none

                  focus:border-blue-500

                  focus:ring-4
                  focus:ring-blue-500/10

                  transition
                "
              />


              <div
                className="
                  text-right
                  mt-1
                  text-xs
                  text-slate-400
                  dark:text-slate-500
                "
              >
                {title.length}/100
              </div>

            </div>


            {/* KATEQORİYA + VƏZİYYƏT */}

            <div
              className="
                grid
                grid-cols-1
                sm:grid-cols-2
                gap-4
              "
            >

              <div>

                <label
                  className="
                    block
                    text-sm
                    font-semibold
                    text-slate-700
                    dark:text-slate-300
                    mb-2
                  "
                >
                  Kateqoriya *
                </label>


                <select
                  value={
                    category
                  }
                  onChange={(e) =>
                    setCategory(
                      e.target.value
                    )
                  }
                  className="
                    w-full
                    h-12
                    px-4

                    rounded-xl

                    border
                    border-slate-200
                    dark:border-white/[0.08]

                    bg-white
                    dark:bg-[#0c0f17]

                    text-slate-900
                    dark:text-white

                    outline-none

                    focus:border-blue-500

                    focus:ring-4
                    focus:ring-blue-500/10

                    transition
                  "
                >

                  <option value="">
                    Kateqoriya seçin
                  </option>


                  {categories.map(
                    (item) => (
                      <option
                        key={item}
                        value={item}
                      >
                        {item}
                      </option>
                    )
                  )}

                </select>

              </div>


              <div>

                <label
                  className="
                    block
                    text-sm
                    font-semibold
                    text-slate-700
                    dark:text-slate-300
                    mb-2
                  "
                >
                  Məhsulun vəziyyəti
                </label>


                <select
                  value={
                    condition
                  }
                  onChange={(e) =>
                    setCondition(
                      e.target.value
                    )
                  }
                  className="
                    w-full
                    h-12
                    px-4

                    rounded-xl

                    border
                    border-slate-200
                    dark:border-white/[0.08]

                    bg-white
                    dark:bg-[#0c0f17]

                    text-slate-900
                    dark:text-white

                    outline-none

                    focus:border-blue-500

                    focus:ring-4
                    focus:ring-blue-500/10

                    transition
                  "
                >

                  {conditions.map(
                    (item) => (
                      <option
                        key={item}
                        value={item}
                      >
                        {item}
                      </option>
                    )
                  )}

                </select>

              </div>

            </div>


            {/* BREND + MODEL */}

            <div
              className="
                grid
                grid-cols-1
                sm:grid-cols-2
                gap-4
              "
            >

              <div>

                <label
                  className="
                    block
                    text-sm
                    font-semibold
                    text-slate-700
                    dark:text-slate-300
                    mb-2
                  "
                >
                  Brend
                </label>


                <input
                  type="text"
                  value={
                    brand
                  }
                  onChange={(e) =>
                    setBrand(
                      e.target.value
                    )
                  }
                  placeholder="Məsələn: Apple"
                  className="
                    w-full
                    h-12
                    px-4

                    rounded-xl

                    border
                    border-slate-200
                    dark:border-white/[0.08]

                    bg-white
                    dark:bg-[#0c0f17]

                    text-slate-900
                    dark:text-white

                    placeholder:text-slate-400
                    dark:placeholder:text-slate-600

                    outline-none

                    focus:border-blue-500

                    focus:ring-4
                    focus:ring-blue-500/10

                    transition
                  "
                />

              </div>


              <div>

                <label
                  className="
                    block
                    text-sm
                    font-semibold
                    text-slate-700
                    dark:text-slate-300
                    mb-2
                  "
                >
                  Model
                </label>


                <input
                  type="text"
                  value={
                    model
                  }
                  onChange={(e) =>
                    setModel(
                      e.target.value
                    )
                  }
                  placeholder="Məsələn: iPhone 15 Pro"
                  className="
                    w-full
                    h-12
                    px-4

                    rounded-xl

                    border
                    border-slate-200
                    dark:border-white/[0.08]

                    bg-white
                    dark:bg-[#0c0f17]

                    text-slate-900
                    dark:text-white

                    placeholder:text-slate-400
                    dark:placeholder:text-slate-600

                    outline-none

                    focus:border-blue-500

                    focus:ring-4
                    focus:ring-blue-500/10

                    transition
                  "
                />

              </div>

            </div>


            {/* AÇIQLAMA */}

            <div>

              <label
                className="
                  block
                  text-sm
                  font-semibold
                  text-slate-700
                  dark:text-slate-300
                  mb-2
                "
              >
                Açıqlama
              </label>


              <textarea
                value={
                  description
                }
                onChange={(e) =>
                  setDescription(
                    e.target.value
                  )
                }
                placeholder="Məhsul haqqında ətraflı məlumat yazın..."
                rows={6}
                maxLength={3000}
                className="
                  w-full

                  px-4
                  py-3

                  rounded-xl

                  border
                  border-slate-200
                  dark:border-white/[0.08]

                  bg-white
                  dark:bg-[#0c0f17]

                  text-slate-900
                  dark:text-white

                  placeholder:text-slate-400
                  dark:placeholder:text-slate-600

                  outline-none

                  resize-none

                  focus:border-blue-500

                  focus:ring-4
                  focus:ring-blue-500/10

                  transition
                "
              />


              <div
                className="
                  text-right
                  mt-1
                  text-xs
                  text-slate-400
                  dark:text-slate-500
                "
              >
                {description.length}/3000
              </div>

            </div>

          </div>

        </section>


        {/* ==============================================================
            QİYMƏT
        ============================================================== */}

        <section
          className="
            bg-white
            dark:bg-[#11141d]

            rounded-3xl

            border
            border-slate-200
            dark:border-white/[0.07]

            shadow-sm
            dark:shadow-black/20

            p-4
            sm:p-6

            mb-5

            transition-colors
            duration-300
          "
        >

          <h2
            className="
              text-lg
              font-bold
              text-slate-900
              dark:text-white
              mb-5
            "
          >
            Qiymət və satış
          </h2>


          <div
            className="
              grid
              grid-cols-1
              sm:grid-cols-2
              gap-4
            "
          >

            <div>

              <label
                className="
                  block
                  text-sm
                  font-semibold
                  text-slate-700
                  dark:text-slate-300
                  mb-2
                "
              >
                Qiymət *
              </label>


              <div className="relative">

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={
                    price
                  }
                  onChange={(e) =>
                    setPrice(
                      e.target.value
                    )
                  }
                  placeholder="0"
                  className="
                    w-full
                    h-12
                    px-4
                    pr-14

                    rounded-xl

                    border
                    border-slate-200
                    dark:border-white/[0.08]

                    bg-white
                    dark:bg-[#0c0f17]

                    text-slate-900
                    dark:text-white

                    placeholder:text-slate-400
                    dark:placeholder:text-slate-600

                    outline-none

                    focus:border-blue-500

                    focus:ring-4
                    focus:ring-blue-500/10

                    transition
                  "
                />


                <span
                  className="
                    absolute
                    right-4
                    top-1/2
                    -translate-y-1/2

                    text-sm
                    font-semibold

                    text-slate-500
                    dark:text-slate-400
                  "
                >
                  AZN
                </span>

              </div>

            </div>


            <div>

              <label
                className="
                  block
                  text-sm
                  font-semibold
                  text-slate-700
                  dark:text-slate-300
                  mb-2
                "
              >
                Mövcud say
              </label>


              <input
                type="number"
                min="1"
                value={
                  stock
                }
                onChange={(e) =>
                  setStock(
                    e.target.value
                  )
                }
                className="
                  w-full
                  h-12
                  px-4

                  rounded-xl

                  border
                  border-slate-200
                  dark:border-white/[0.08]

                  bg-white
                  dark:bg-[#0c0f17]

                  text-slate-900
                  dark:text-white

                  outline-none

                  focus:border-blue-500

                  focus:ring-4
                  focus:ring-blue-500/10

                  transition
                "
              />

            </div>

          </div>


          {/* QİYMƏTDƏ RAZILAŞMA */}

          <label
            className="
              mt-5

              flex
              items-center
              gap-3

              cursor-pointer
              select-none

              p-3
              rounded-2xl

              hover:bg-slate-50
              dark:hover:bg-white/[0.03]

              transition
            "
          >

            <input
              type="checkbox"
              checked={
                negotiable
              }
              onChange={(e) =>
                setNegotiable(
                  e.target.checked
                )
              }
              className="
                w-5
                h-5
                accent-blue-600
              "
            />


            <div>

              <p
                className="
                  text-sm
                  font-semibold
                  text-slate-800
                  dark:text-slate-200
                "
              >
                Qiymətdə razılaşmaq olar
              </p>


              <p
                className="
                  text-xs
                  text-slate-500
                  dark:text-slate-400
                "
              >
                Alıcı ilə qiymət barədə
                razılaşa bilərsiniz.
              </p>

            </div>

          </label>

        </section>


        {/* ==============================================================
            MƏKAN
        ============================================================== */}

        <section
          className="
            bg-white
            dark:bg-[#11141d]

            rounded-3xl

            border
            border-slate-200
            dark:border-white/[0.07]

            shadow-sm
            dark:shadow-black/20

            p-4
            sm:p-6

            mb-5

            transition-colors
            duration-300
          "
        >

          <div
            className="
              flex
              items-center
              gap-3
              mb-6
            "
          >

            <div
              className="
                w-10
                h-10
                rounded-xl

                bg-emerald-50
                dark:bg-emerald-500/10

                text-emerald-600
                dark:text-emerald-400

                flex
                items-center
                justify-center
              "
            >

              <MapPin
                size={20}
              />

            </div>


            <div>

              <h2
                className="
                  text-lg
                  font-bold
                  text-slate-900
                  dark:text-white
                "
              >
                Məkan
              </h2>


              <p
                className="
                  text-sm
                  text-slate-500
                  dark:text-slate-400
                "
              >
                Məhsulun yerləşdiyi yeri
                göstərin
              </p>

            </div>

          </div>


          <div
            className="
              grid
              grid-cols-1
              sm:grid-cols-2
              gap-4
            "
          >

            <div>

              <label
                className="
                  block
                  text-sm
                  font-semibold
                  text-slate-700
                  dark:text-slate-300
                  mb-2
                "
              >
                Şəhər / rayon
              </label>


              <input
                type="text"
                value={
                  location
                }
                onChange={(e) =>
                  setLocation(
                    e.target.value
                  )
                }
                placeholder="Məsələn: Bakı, Nəsimi"
                className="
                  w-full
                  h-12
                  px-4

                  rounded-xl

                  border
                  border-slate-200
                  dark:border-white/[0.08]

                  bg-white
                  dark:bg-[#0c0f17]

                  text-slate-900
                  dark:text-white

                  placeholder:text-slate-400
                  dark:placeholder:text-slate-600

                  outline-none

                  focus:border-blue-500

                  focus:ring-4
                  focus:ring-blue-500/10

                  transition
                "
              />

            </div>


            <div>

              <label
                className="
                  block
                  text-sm
                  font-semibold
                  text-slate-700
                  dark:text-slate-300
                  mb-2
                "
              >
                Ərazi / məsafə
              </label>


              <input
                type="text"
                value={
                  distance
                }
                onChange={(e) =>
                  setDistance(
                    e.target.value
                  )
                }
                placeholder="Məsələn: 2 km"
                className="
                  w-full
                  h-12
                  px-4

                  rounded-xl

                  border
                  border-slate-200
                  dark:border-white/[0.08]

                  bg-white
                  dark:bg-[#0c0f17]

                  text-slate-900
                  dark:text-white

                  placeholder:text-slate-400
                  dark:placeholder:text-slate-600

                  outline-none

                  focus:border-blue-500

                  focus:ring-4
                  focus:ring-blue-500/10

                  transition
                "
              />

            </div>

          </div>

        </section>


        {/* ==============================================================
            ÇATDIRILMA
        ============================================================== */}

        <section
          className="
            bg-white
            dark:bg-[#11141d]

            rounded-3xl

            border
            border-slate-200
            dark:border-white/[0.07]

            shadow-sm
            dark:shadow-black/20

            p-4
            sm:p-6

            mb-5

            transition-colors
            duration-300
          "
        >

          <div
            className="
              flex
              items-center
              gap-3
              mb-5
            "
          >

            <div
              className="
                w-10
                h-10
                rounded-xl

                bg-orange-50
                dark:bg-orange-500/10

                text-orange-600
                dark:text-orange-400

                flex
                items-center
                justify-center
              "
            >

              <Truck
                size={20}
              />

            </div>


            <div>

              <h2
                className="
                  text-lg
                  font-bold
                  text-slate-900
                  dark:text-white
                "
              >
                Çatdırılma
              </h2>


              <p
                className="
                  text-sm
                  text-slate-500
                  dark:text-slate-400
                "
              >
                Alıcı məhsulu necə əldə edə
                bilər?
              </p>

            </div>

          </div>


          <div
            className="
              grid
              grid-cols-1
              sm:grid-cols-3
              gap-3
            "
          >

            {deliveryOptions.map(
              (item) => {

                const active =
                  delivery ===
                  item;

                return (
                  <button
                    type="button"
                    key={
                      item
                    }
                    onClick={() =>
                      setDelivery(
                        item
                      )
                    }
                    className={`
                      min-h-12
                      px-4

                      rounded-xl

                      border

                      text-sm
                      font-medium

                      transition

                      flex
                      items-center
                      justify-center
                      gap-2

                      ${
                        active
                          ? `
                            border-blue-600
                            bg-blue-50
                            text-blue-700

                            dark:border-blue-500
                            dark:bg-blue-500/10
                            dark:text-blue-400
                          `
                          : `
                            border-slate-200
                            text-slate-600
                            hover:border-slate-300

                            dark:border-white/[0.08]
                            dark:text-slate-400
                            dark:hover:border-white/[0.16]
                            dark:hover:bg-white/[0.03]
                          `
                      }
                    `}
                  >

                    {active && (
                      <Check
                        size={16}
                      />
                    )}

                    {item}

                  </button>
                );
              }
            )}

          </div>

        </section>


        {/* ==============================================================
            ƏLAQƏ MƏLUMATLARI
        ============================================================== */}

        <section
          className="
            bg-white
            dark:bg-[#11141d]

            rounded-3xl

            border
            border-slate-200
            dark:border-white/[0.07]

            shadow-sm
            dark:shadow-black/20

            p-4
            sm:p-6

            mb-5

            transition-colors
            duration-300
          "
        >

          <div
            className="
              flex
              items-center
              gap-3
              mb-6
            "
          >

            <div
              className="
                w-10
                h-10
                rounded-xl

                bg-violet-50
                dark:bg-violet-500/10

                text-violet-600
                dark:text-violet-400

                flex
                items-center
                justify-center
              "
            >

              <Phone
                size={20}
              />

            </div>


            <div>

              <h2
                className="
                  text-lg
                  font-bold
                  text-slate-900
                  dark:text-white
                "
              >
                Əlaqə məlumatları
              </h2>


              <p
                className="
                  text-sm
                  text-slate-500
                  dark:text-slate-400
                "
              >
                Alıcıların sizinlə əlaqə
                saxlaması üçün
              </p>

            </div>

          </div>


          <div>

            <label
              className="
                block
                text-sm
                font-semibold
                text-slate-700
                dark:text-slate-300
                mb-2
              "
            >
              Telefon *
            </label>


            <input
              type="tel"
              value={
                phone
              }
              onChange={(e) =>
                setPhone(
                  formatPhone(
                    e.target.value
                  )
                )
              }
              placeholder="051-598-35-50"
              maxLength={13}
              className="
                w-full
                h-12
                px-4

                rounded-xl

                border
                border-slate-200
                dark:border-white/[0.08]

                bg-white
                dark:bg-[#0c0f17]

                text-slate-900
                dark:text-white

                placeholder:text-slate-400
                dark:placeholder:text-slate-600

                outline-none

                focus:border-blue-500

                focus:ring-4
                focus:ring-blue-500/10

                transition
              "
            />

          </div>

        </section>


        {/* ==============================================================
            ZƏMANƏT
        ============================================================== */}

        <section
          className="
            bg-white
            dark:bg-[#11141d]

            rounded-3xl

            border
            border-slate-200
            dark:border-white/[0.07]

            shadow-sm
            dark:shadow-black/20

            p-4
            sm:p-6

            mb-6

            transition-colors
            duration-300
          "
        >

          <div
            className="
              flex
              items-center
              gap-3
              mb-5
            "
          >

            <div
              className="
                w-10
                h-10
                rounded-xl

                bg-green-50
                dark:bg-green-500/10

                text-green-600
                dark:text-green-400

                flex
                items-center
                justify-center
              "
            >

              <ShieldCheck
                size={20}
              />

            </div>


            <div>

              <h2
                className="
                  text-lg
                  font-bold
                  text-slate-900
                  dark:text-white
                "
              >
                Zəmanət
              </h2>


              <p
                className="
                  text-sm
                  text-slate-500
                  dark:text-slate-400
                "
              >
                Əgər varsa, zəmanət
                məlumatlarını qeyd edin
              </p>

            </div>

          </div>


          <input
            type="text"
            value={
              warranty
            }
            onChange={(e) =>
              setWarranty(
                e.target.value
              )
            }
            placeholder="Məsələn: 6 ay zəmanət"
            maxLength={100}
            className="
              w-full
              h-12
              px-4

              rounded-xl

              border
              border-slate-200
              dark:border-white/[0.08]

              bg-white
              dark:bg-[#0c0f17]

              text-slate-900
              dark:text-white

              placeholder:text-slate-400
              dark:placeholder:text-slate-600

              outline-none

              focus:border-blue-500

              focus:ring-4
              focus:ring-blue-500/10

              transition
            "
          />

        </section>


        {/* ==============================================================
            DƏRC ET
        ============================================================== */}

        <div
          className="
            sticky
            bottom-3
            sm:static
          "
        >

          <button
            type="submit"
            disabled={
              uploading ||
              creating ||
              freeAdLimitReached
            }
            className="
              w-full
              h-14

              rounded-2xl

              bg-blue-600
              hover:bg-blue-700

              disabled:bg-slate-300
              disabled:dark:bg-slate-700

              disabled:cursor-not-allowed

              text-white

              font-bold
              text-base

              shadow-lg
              shadow-blue-600/20

              transition

              flex
              items-center
              justify-center
              gap-3
            "
          >

            {freeAdLimitReached ? (
              <>
                <X
                  size={20}
                />

                Haqqınız bitib
              </>
            ) : uploading ||
              creating ? (
              <>
                <Loader2
                  size={20}
                  className="animate-spin"
                />

                {uploading
                  ? "Şəkillər yüklənir..."
                  : "Elan dərc edilir..."}
              </>
            ) : (
              <>
                <Package
                  size={20}
                />

                Elanı dərc et
              </>
            )}

          </button>

        </div>


        <p
          className="
            text-center
            text-xs
            text-slate-400
            dark:text-slate-500
            mt-4
          "
        >
          Elanı dərc etməklə təqdim
          etdiyiniz məlumatların düzgün
          olduğunu təsdiqləyirsiniz.
        </p>

      </form>

    </div>
  );
};


export default Addpagenation;